import { useEffect, useRef, useState } from 'react';
import type { MonitoringFilters } from '../hooks/useMonitoringFilters';
import type { ColumnKey, SearchField, WarrantyFilter } from '../monitoringUtils';
import { MONITORING_COLUMNS } from './columns';
import { UptimeDropdown, UptimeDropdownCheckItem, UptimeSelect } from '../../shared';
import useFmText from './useFmText';
import './MonitoringToolbar.scss';

interface IMonitoringToolbarProps {
    filters: MonitoringFilters;
    onChange: (patch: Partial<MonitoringFilters>) => void;
    searchFields: SearchField[];
    vehicleTypeCounts: { type: string; count: number }[];
    visibleColumns: Set<ColumnKey>;
    availableColumns: ColumnKey[];
    onToggleColumn: (key: ColumnKey) => void;
    moreFiltersCount: number;
    onOpenMoreFilters: () => void;
}

const MonitoringToolbar = ({
    filters,
    onChange,
    searchFields,
    vehicleTypeCounts,
    visibleColumns,
    availableColumns,
    onToggleColumn,
    moreFiltersCount,
    onOpenMoreFilters,
}: IMonitoringToolbarProps) => {
    const { t, formatNumber, vehicleType } = useFmText();
    const [query, setQuery] = useState(filters.q);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => setQuery(filters.q), [filters.q]);

    useEffect(() => {
        if (query === filters.q) {
            return;
        }
        const timer = setTimeout(() => onChange({ q: query }), 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;
            const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
            const overlayOpen = !!document.querySelector('.upt-drawer, .modal.show');
            if (event.key === '/' && !typing && !overlayOpen) {
                event.preventDefault();
                inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                inputRef.current?.focus({ preventScroll: true });
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    const toggleType = (type: string) =>
        onChange({
            types: filters.types.includes(type)
                ? filters.types.filter(item => item !== type)
                : [...filters.types, type],
        });

    return (
        <div className='fm-toolbar'>
            <div className='fm-omni'>
                <UptimeSelect
                    className='fm-omni-field'
                    triggerClassName='fm-omni-trigger'
                    value={filters.qField}
                    options={searchFields.map(field => ({ value: field, label: t(`search.field.${field}`) }))}
                    onChange={(qField: SearchField) => onChange({ qField })}
                />
                <input
                    ref={inputRef}
                    type='text'
                    value={query}
                    placeholder={t('search.placeholder')}
                    aria-label={t('search.placeholder')}
                    autoComplete='off'
                    onChange={event => setQuery(event.target.value)}
                />
                <kbd title={t('search.shortcutHint')}>/</kbd>
            </div>

            <UptimeDropdown label={t('filters.vehicleType')} badge={filters.types.length}>
                {vehicleTypeCounts.map(({ type, count }) => (
                    <UptimeDropdownCheckItem
                        key={type}
                        label={vehicleType(type)}
                        meta={formatNumber(count)}
                        checked={filters.types.includes(type)}
                        onChange={() => toggleType(type)}
                    />
                ))}
            </UptimeDropdown>

            <UptimeSelect
                label={t('filters.warranty')}
                value={filters.warranty}
                options={[
                    { value: '' as WarrantyFilter, label: t('filters.warrantyAll') },
                    { value: 'in' as WarrantyFilter, label: t('warranty.in') },
                    { value: 'out' as WarrantyFilter, label: t('warranty.out') },
                ]}
                onChange={(warranty: WarrantyFilter) => onChange({ warranty })}
            />

            <button
                type='button'
                className={`fm-btn${filters.withCampaign ? ' fm-on' : ''}`}
                aria-pressed={filters.withCampaign}
                onClick={() => onChange({ withCampaign: !filters.withCampaign })}
            >
                {t('filters.pendingCampaign')}
            </button>

            <button type='button' className='fm-btn' onClick={onOpenMoreFilters}>
                {t('filters.more')}
                {moreFiltersCount > 0 && <span className='fm-badge'>{moreFiltersCount}</span>}
            </button>

            <span className='fm-spacer' />

            <UptimeDropdown label={t('columns.button')} align='right'>
                {MONITORING_COLUMNS.filter(column => !column.fixed && availableColumns.includes(column.key)).map(
                    column => (
                        <UptimeDropdownCheckItem
                            key={column.key}
                            label={t(`columns.${column.key}`)}
                            checked={visibleColumns.has(column.key)}
                            onChange={() => onToggleColumn(column.key)}
                        />
                    )
                )}
            </UptimeDropdown>
        </div>
    );
};

export default MonitoringToolbar;
